import React, { useState, useEffect, useRef } from "react";

export const SCORE_TIERS = [
  { id: "above",    label: "Above Average", min: 95, range: "95+",   color: "#1D9E75", bg: "#ecfdf5", border: "#a7f3d0" },
  { id: "average",  label: "Average",       min: 90, range: "90-94", color: "#185FA5", bg: "#eff6ff", border: "#bfdbfe" },
  { id: "below",    label: "Below Average", min: 80, range: "80-89", color: "#854F0B", bg: "#fffbeb", border: "#fde68a" },
  { id: "critical", label: "Critical",      min: 0,  range: "<80",   color: "#A32D2D", bg: "#fef2f2", border: "#fecaca" },
];

export function scoreTier(score) {
  if (score == null) return null;
  return SCORE_TIERS.find(t => score >= t.min) || SCORE_TIERS[SCORE_TIERS.length - 1];
}

export function scoreColor(score) {
  const t = scoreTier(score);
  return t ? t.color : "#94a3b8";
}

// ── ScoreRing ─────────────────────────────────────────────────────────────────
export function ScoreRing({ score, size = 52, strokeWidth = 4 }) {
  // No scorable points is not a zero. Render the ring empty rather than absent:
  // an unscored track still occupies its place in the row, so the slot reads as
  // "nothing to show yet" instead of looking like a missing element.
  if (score == null) {
    const rNull = (size - strokeWidth * 2) / 2;
    return (
      <div className="score-ring" style={{ width: size, height: size }}>
        <svg width={size} height={size}>
          <circle cx={size/2} cy={size/2} r={rNull} fill="none"
            stroke="var(--gray-200, #e5e7eb)" strokeWidth={strokeWidth} />
        </svg>
        <span className="score-ring-value" style={{ fontFamily: "var(--font-mono)",
          fontSize: size < 50 ? 11 : size < 70 ? 13 : 16,
          color: "var(--text-tertiary)" }}>-</span>
      </div>
    );
  }
  const r   = (size - strokeWidth * 2) / 2;
  const c   = r * 2 * Math.PI;
  const off = c - (score / 100) * c;
  const tier = scoreTier(score);
  const color = tier ? tier.color : "#94a3b8";
  const track = tier ? tier.bg    : "#f1f5f9";
  const fontSize = size < 50 ? 11 : size < 70 ? 13 : 16;

  return (
    <div className="score-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={track} strokeWidth={strokeWidth} />
        {score > 0 && (
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
            strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 0.5s ease" }}
          />
        )}
      </svg>
      <span className="score-ring-value" style={{ fontSize, color, fontFamily: "var(--font-mono)", fontWeight: 700 }}>{score}%</span>
    </div>
  );
}

export function Checkbox({ checked, onChange, size = 15, indeterminate }) {
  return (
    <span onClick={e => { e.stopPropagation(); onChange && onChange(!checked); }}
      style={{ width:size, height:size, borderRadius:3, flexShrink:0, cursor:"pointer",
        border:`1.5px solid ${checked || indeterminate ? "#3b82f6" : "var(--border, #cbd5e1)"}`,
        background: checked || indeterminate ? "#3b82f6" : "transparent",
        display:"inline-flex", alignItems:"center", justifyContent:"center", transition:"all 0.1s" }}>
      {checked && <svg width={size-5} height={size-5} viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="3"><path d="M3 8l3.5 3.5L13 5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
      {indeterminate && !checked && <svg width={size-6} height={size-6} viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="3"><line x1="4" y1="8" x2="12" y2="8" strokeLinecap="round"/></svg>}
    </span>
  );
}

/* ── Filter Chip ──────────────────────────────────────────────── */
export function Chip({ label, value, onRemove }) {
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"2px 5px 2px 7px", fontSize:10.5,
      fontFamily:"var(--font-mono)", background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe", borderRadius:4 }}>
      <span style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", opacity:0.5 }}>{label}:</span>
      <span style={{ fontWeight:500 }}>{value}</span>
      <span onClick={onRemove} style={{ cursor:"pointer", opacity:0.4, display:"flex", marginLeft:1 }}>
        <svg width="9" height="9" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="4" y1="4" x2="12" y2="12" strokeLinecap="round"/><line x1="12" y1="4" x2="4" y2="12" strokeLinecap="round"/>
        </svg>
      </span>
    </span>
  );
}

// ── StatusBadge ───────────────────────────────────────────────────────────────
const STATUS = {
  QA_PASSED:          { label: "Passed",     cls: "passed" },
  QA_FAILED:          { label: "Failed",     cls: "failed" },
  PROCESSING:         { label: "Processing", cls: "processing" },
  RECEIVED:           { label: "Received",   cls: "pending" },
  EXTRACTION_FAILED:  { label: "Ext. Error", cls: "failed" },
  INDEXED:            { label: "Indexed",    cls: "passed" },
  CLOSED:             { label: "Closed",     cls: "passed" },
  REVIEWED:           { label: "Reviewed",   cls: "passed" },
};

export function StatusBadge({ status }) {
  const s = STATUS[status] || { label: status, cls: "pending" };
  return (
    <span className={`badge ${s.cls}`}>
      <span className="badge-dot" /> {s.label}
    </span>
  );
}

// Per-track review indicator. Distinguishes "signed off" from "still open"
// from "doesn't apply" — a dash is not the same as an empty circle.
export function TrackCheck({ done, na }) {
  if (na) return <span style={{ color:"var(--text-tertiary)", fontSize:11 }}>—</span>;
  if (done) return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#16a34a" strokeWidth="2.5" style={{ display:"block", margin:"0 auto" }}>
      <path d="M3 8.5l3.5 3.5L13 4.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
  return (
    <span title="Not signed off" style={{ display:"block", width:11, height:11, margin:"0 auto",
      borderRadius:"50%", border:"1.5px solid var(--border-strong, #cbd5e1)" }}/>
  );
}

export function TrackScore({ score, done, na }) {
  if (na || score == null) {
    return <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)" }}>-</span>;
  }
  const color = scoreColor(score);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, fontWeight: 700, color }}>{score}%</span>
      {done ? (
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="#16a34a" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round"><polyline points="3 8 6.5 11.5 13 5"/></svg>
      ) : (
        <span style={{ width: 9, height: 9, borderRadius: "50%", border: "1.5px solid var(--border)" }}/>
      )}
    </span>
  );
}

export function IdrCell({ status, hasNarrative, na }) {
  if (na) return <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)" }}>-</span>;

  if (status === "reviewed") {
    return (
      <span title="IDR reviewed" style={{ display: "inline-flex", justifyContent: "center" }}>
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#16a34a" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round"><polyline points="3 8 6.5 11.5 13 5"/></svg>
      </span>
    );
  }
  if (hasNarrative || status === "draft") {
    return <span title="IDR drafted, not reviewed" style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "#d97706" }}>Draft</span>;
  }
  return <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)" }}>-</span>;
}

/* ── Filter Dropdown ──────────────────────────────────────────── */
// selectAll adds a "Select all" row, for lists where it is easier to take
// everything and untick a few than to tick most of them one by one. Opt-in,
// because an empty selection already means "all" for most of these filters.
export function FilterDropdown({ label, selected, options, onChange, width = 150, selectAll = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const active = selected.length > 0;
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ(""); } };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const list = q ? options.filter(o => o.toLowerCase().includes(q.toLowerCase())) : options;
  const toggle = v => selected.includes(v) ? onChange(selected.filter(x => x !== v)) : onChange([...selected, v]);
  // Acts on what the search is showing, so "Select all" after typing a name
  // takes just the matches and leaves anything already chosen alone.
  const visibleAll  = list.length > 0 && list.every(o => selected.includes(o));
  const visibleSome = list.some(o => selected.includes(o));
  const toggleVisible = () => visibleAll
    ? onChange(selected.filter(x => !list.includes(x)))
    : onChange([...selected, ...list.filter(o => !selected.includes(o))]);
  const everything = selectAll && options.length > 0 && selected.length === options.length;
  return (
    <div ref={ref} style={{ position:"relative" }}>
      <button onClick={() => setOpen(!open)} style={{
        display:"flex", alignItems:"center", gap:5, padding:"5px 9px", fontSize:11, fontFamily:"var(--font-mono)",
        border:`1px solid ${active ? "#3b82f6" : "var(--border)"}`, borderRadius:6,
        background: active ? "#eff6ff" : "var(--white)", color: active ? "#1d4ed8" : "var(--text-secondary)",
        fontWeight: active ? 600 : 400, cursor:"pointer", outline:"none", minWidth:width, justifyContent:"space-between" }}>
        <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {everything ? `All (${options.length})`
            : active ? (selected.length === 1 ? selected[0] : `${selected.length} selected`) : label}
        </span>
        <span style={{ display:"flex", alignItems:"center", gap:3, flexShrink:0 }}>
          {active && <span onClick={e => { e.stopPropagation(); onChange([]); }} style={{ opacity:0.5, display:"flex" }} title="Clear">
            <svg width="9" height="9" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="4" y1="4" x2="12" y2="12" strokeLinecap="round"/><line x1="12" y1="4" x2="4" y2="12" strokeLinecap="round"/></svg>
          </span>}
          <svg width="9" height="9" viewBox="0 0 16 16" fill="none" stroke={active ? "#3b82f6" : "var(--text-tertiary)"} strokeWidth="2"
            style={{ transform: open ? "rotate(180deg)" : "none", transition:"transform 0.15s" }}>
            <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>
      {open && (
        <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, width:Math.max(width, 195), maxHeight:250,
          background:"var(--white)", border:"1px solid var(--border)", borderRadius:8,
          boxShadow:"0 8px 24px rgba(0,0,0,0.1)", zIndex:1000, display:"flex", flexDirection:"column" }}>
          {options.length > 5 && <div style={{ padding:"6px 8px", borderBottom:"1px solid var(--border)" }}>
            <input placeholder="Search..." value={q} onChange={e => setQ(e.target.value)} autoFocus
              style={{ width:"100%", boxSizing:"border-box", padding:"5px 8px", fontSize:11, fontFamily:"var(--font-mono)",
                border:"1px solid var(--border)", borderRadius:5, outline:"none", background:"#f9fafb" }}/>
          </div>}
          <div style={{ overflowY:"auto", maxHeight:200 }}>
            {selectAll && list.length > 0 && (
              <div onClick={toggleVisible} style={{
                padding:"6px 10px", fontSize:11, fontFamily:"var(--font-mono)", cursor:"pointer",
                display:"flex", alignItems:"center", gap:7, fontWeight:600,
                color:"var(--text-primary)", borderBottom:"1px solid var(--border)" }}
                onMouseEnter={e => { e.currentTarget.style.background = "#f9fafb"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                <Checkbox checked={visibleAll} indeterminate={visibleSome && !visibleAll} size={13} onChange={toggleVisible}/>
                {q ? `Select all matches (${list.length})` : `Select all (${list.length})`}
              </div>
            )}
            {list.length === 0 ? <div style={{ padding:12, fontSize:11, color:"var(--text-tertiary)", textAlign:"center" }}>No matches</div>
            : list.map(opt => {
              const sel = selected.includes(opt);
              return (
                <div key={opt} onClick={() => toggle(opt)} style={{
                  padding:"6px 10px", fontSize:11, fontFamily:"var(--font-mono)", cursor:"pointer",
                  display:"flex", alignItems:"center", gap:7,
                  background: sel ? "#eff6ff" : "transparent", color: sel ? "#1d4ed8" : "var(--text-secondary)",
                  fontWeight: sel ? 500 : 400 }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = "#f9fafb"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = sel ? "#eff6ff" : "transparent"; }}>
                  <Checkbox checked={sel} size={13} onChange={() => toggle(opt)}/>{opt}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   COMBOBOX (typeable dropdown with "add new" option)
   ═══════════════════════════════════════════════════════════════════ */
// onSelect (optional) fires only when an option is picked or a new one is added,
// not on every keystroke - for callers that save on selection.
export function Combobox({ value, onChange, onSelect, options, placeholder, allowNew = true }) {
  const [input, setInput] = useState(value || "");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => { setInput(value || ""); }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const lower = input.toLowerCase().trim();
  const matches = options.filter(o => o.toLowerCase().includes(lower));
  const hasExactMatch = options.some(o => o.toLowerCase() === lower);
  const showAddNew = allowNew && lower && !hasExactMatch;

  const select = (val) => {
    setInput(val);
    onChange(val);
    onSelect?.(val);
    setOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <input
        value={input}
        onChange={e => { setInput(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        style={{ width: "100%", padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, background: "var(--white)", color: "var(--text-primary)", boxSizing: "border-box" }}
      />
      {open && (matches.length > 0 || showAddNew) && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, marginTop: 2,
          background: "var(--white)", border: "1px solid var(--border)", borderRadius: 6,
          maxHeight: 200, overflowY: "auto", zIndex: 10,
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        }}>
          {matches.map(opt => (
            <div key={opt} onClick={() => select(opt)}
              onMouseEnter={e => e.currentTarget.style.background = "var(--gray-50, #f9fafb)"}
              onMouseLeave={e => e.currentTarget.style.background = ""}
              style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer", color: "var(--text-primary)" }}>
              {opt}
            </div>
          ))}
          {showAddNew && (
            <div onClick={() => select(input.trim())}
              onMouseEnter={e => e.currentTarget.style.background = "var(--blue-50, #eff6ff)"}
              onMouseLeave={e => e.currentTarget.style.background = ""}
              style={{
                padding: "6px 10px", fontSize: 12, cursor: "pointer",
                color: "var(--blue-700, #1d4ed8)",
                borderTop: matches.length > 0 ? "1px solid var(--border)" : "none",
                fontWeight: 500,
              }}>
              + Add "{input.trim()}" as new
            </div>
          )}
        </div>
      )}
    </div>
  );
}
